import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getApiClient } from '../client';
import type { PaginatedResponse } from '../types/common';
import type {
  ClientCompany,
  ClientContact,
  ClientCompanyCreatePayload,
  ClientContactCreatePayload,
} from '../types/client';

/** Query keys */
export const clientsKeys = {
  all: ['clients'] as const,
  companies: () => [...clientsKeys.all, 'companies'] as const,
  company: (id: string) => [...clientsKeys.companies(), id] as const,
  contacts: () => [...clientsKeys.all, 'contacts'] as const,
  contact: (id: string) => [...clientsKeys.contacts(), id] as const,
  companyContacts: (companyId: string) => [...clientsKeys.contacts(), 'company', companyId] as const,
};

/** Fetch all client companies */
export function useClientCompanies(filters: Record<string, unknown> = {}) {
  return useQuery({
    queryKey: [...clientsKeys.companies(), filters],
    queryFn: async () => {
      const client = getApiClient();
      const response = await client.get<PaginatedResponse<ClientCompany>>('/api/v1/clients/', {
        params: filters,
      });
      return response.data;
    },
  });
}

/** Fetch a single client company */
export function useClientCompany(id: string) {
  return useQuery({
    queryKey: clientsKeys.company(id),
    queryFn: async () => {
      const client = getApiClient();
      const response = await client.get<ClientCompany>(`/api/v1/clients/${id}/`);
      return response.data;
    },
    enabled: !!id,
  });
}

/** Create a client company */
export function useCreateClientCompany() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (payload: ClientCompanyCreatePayload) => {
      const client = getApiClient();
      const response = await client.post<ClientCompany>('/api/v1/clients/', payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clientsKeys.companies() });
    },
  });
}

/** Fetch all client contacts */
export function useClientContacts(filters: Record<string, unknown> = {}) {
  return useQuery({
    queryKey: [...clientsKeys.contacts(), filters],
    queryFn: async () => {
      const client = getApiClient();
      const response = await client.get<PaginatedResponse<ClientContact>>('/api/v1/client-contacts/', {
        params: filters,
      });
      return response.data;
    },
  });
}

/** Fetch contacts for a specific company */
export function useCompanyContacts(companyId: string) {
  return useQuery({
    queryKey: clientsKeys.companyContacts(companyId),
    queryFn: async () => {
      const client = getApiClient();
      const response = await client.get<ClientContact[]>(`/api/v1/clients/${companyId}/contacts/`);
      return response.data;
    },
    enabled: !!companyId,
  });
}

/** Fetch a single client contact */
export function useClientContact(id: string) {
  return useQuery({
    queryKey: clientsKeys.contact(id),
    queryFn: async () => {
      const client = getApiClient();
      const response = await client.get<ClientContact>(`/api/v1/client-contacts/${id}/`);
      return response.data;
    },
    enabled: !!id,
  });
}

/** Create a client contact */
export function useCreateClientContact() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (payload: ClientContactCreatePayload) => {
      const client = getApiClient();
      const response = await client.post<ClientContact>('/api/v1/client-contacts/', payload);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: clientsKeys.contacts() });
      queryClient.invalidateQueries({ queryKey: clientsKeys.companyContacts(data.company_id) });
    },
  });
}
